import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
} from "@chakra-ui/react";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCheck,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaCopy,
  FaEdit,
  FaRedoAlt,
  FaTimes,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  BseDto,
  BseStatusDto,
  NegotiationStateName,
  useCancelSourcingEventMutation,
  useCloseSourcingEventMutation,
  useCreateNegotiationRoundMutation,
  useCreateSourcingEventCopyMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { displayDate } from "../../../../common/formatting/displayDate";
import { displayDateWithTime } from "../../../../common/formatting/displayDateWithTime";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { setSourcingEvent } from "../../../../common/redux/reducers/basicSourcingEventReducer";
import { useSub } from "../../../../common/subscription/useSub";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { urls } from "../../../../urls";
import { EventStatusTag } from "../../EventStatusTag";
import { isAwardedBse, isCancelledBse, isClosedBse, isCompletedBse, isPublishedBse } from "../../typeguards";
import { useNegotiation } from "./NegotiationContext";

interface Props {
  event: BseDto;
}

export const EventMeta = ({ event }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const displayer = useApiError();
  const dispatch = useAppDispatch();
  const sub = useSub();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [closeReason, setCloseReason] = useState("");

  const [createEventCopy, { isLoading: isLoadingEventCopy }] = useCreateSourcingEventCopyMutation();
  const [startNegotiationRound, { isLoading: isStartingNegotiationRound }] = useCreateNegotiationRoundMutation();
  const [cancelSourcingEvent, { isLoading: isCancellingSourcingEvent }] = useCancelSourcingEventMutation();
  const [closeSourcingEvent, { isLoading: isClosingSourcingEvent }] = useCloseSourcingEventMutation();

  const { roundNumber, setRoundNumber, currentRound, latestRound } = useNegotiation();

  const currentDeadline = currentRound?.deadline ?? event?.awardedFields?.deadline ?? event?.publishedFields?.deadline;

  const latestDeadline = latestRound?.deadline ?? event?.awardedFields?.deadline ?? event?.publishedFields?.deadline;

  const isLatestDeadlinePassed = (): boolean => {
    if (!latestDeadline) return false;
    return moment().isAfter(moment(latestDeadline));
  };

  const isCurrentDeadlinePassed = (): boolean => {
    if (!currentDeadline) return false;
    return moment().isAfter(moment(currentDeadline));
  };

  const handleCancelEvent = async () => {
    const res = await cancelSourcingEvent({
      eventId: event.id,
      cancelSourcingEventRequest: {
        reason: cancelReason.trim() || null,
      },
    });

    if (!("data" in res)) {
      displayer.trigger(res.error);
    } else {
      setIsCancelModalOpen(false);
      setCancelReason("");
      dispatch(setSourcingEvent(res.data));
    }
  };

  const handleCloseEvent = async () => {
    const res = await closeSourcingEvent({
      eventId: event.id,
      closeSourcingEventRequest: {
        reason: closeReason.trim() || null,
      },
    });

    if (!("data" in res)) {
      displayer.trigger(res.error);
    } else {
      dispatch(setSourcingEvent(res.data));
      setIsCloseModalOpen(false);
      setCloseReason("");
    }
  };

  const isEventCancelled = isCancelledBse(event);
  const isEventClosed = isClosedBse(event);

  return (
    <>
      <Flex justifyContent="space-between">
        <Flex alignItems="center">
          {sub.hasNegotiationAcces && (
            <>
              <IconButton
                aria-label="previous round"
                icon={<Icon as={FaChevronLeft} />}
                size="sm"
                variant="ghost"
                cursor="pointer"
                colorScheme="teal"
                isDisabled={roundNumber === 0}
                onClick={() => setRoundNumber(roundNumber - 1)}
              />
              <Text fontWeight="semibold" mx="2">
                {roundNumber === 0 ? t("Original sourcing event") : `${t("Negotiation round")} ${roundNumber}`}
              </Text>
              <IconButton
                aria-label="next round"
                icon={<Icon as={FaChevronRight} />}
                size="sm"
                variant="ghost"
                cursor="pointer"
                colorScheme="teal"
                isDisabled={roundNumber >= (event.negotiationContext?.rounds.length ?? 0)}
                onClick={() => setRoundNumber(roundNumber + 1)}
              />
            </>
          )}
        </Flex>
        <Menu>
          <MenuButton as={Button} variant="ghost" aria-label="Event actions">
            <Flex columnGap={1} cursor="pointer">
              <Flex width="1.5" height="1.5" backgroundColor="smText" rounded="full"></Flex>
              <Flex width="1.5" height="1.5" backgroundColor="smText" rounded="full"></Flex>
              <Flex width="1.5" height="1.5" backgroundColor="smText" rounded="full"></Flex>
            </Flex>
          </MenuButton>
          <MenuList>
            {currentRound ? (
              <MenuItem
                isDisabled={!event.canEdit || isAwardedBse(event) || isEventCancelled || isEventClosed}
                icon={<Icon as={FaEdit} />}
                onClick={() =>
                  navigate(urls.events.editNegotiation.go({ eventId: event.id, negotiationId: currentRound.id }).intro)
                }
              >
                <Explanation
                  enabled={!event.canEdit || isEventCancelled || isEventClosed}
                  text={
                    isEventCancelled
                      ? t("Cannot edit a cancelled event")
                      : isEventClosed
                        ? t("Cannot edit a closed event")
                        : (t("Only admins and creators can edit negotiation rounds") ?? "")
                  }
                  shouldWrapChildren
                >
                  {t("Edit negotiation round")}
                </Explanation>
              </MenuItem>
            ) : (
              <MenuItem
                isDisabled={!event.canEdit || !isPublishedBse(event) || isEventCancelled || isEventClosed}
                icon={<Icon as={FaEdit} />}
                onClick={() => navigate(urls.events.edit.go(event.id).intro)}
              >
                <Explanation
                  enabled={!event.canEdit || isEventCancelled || isEventClosed}
                  text={
                    isEventCancelled
                      ? t("Cannot edit a cancelled event")
                      : isEventClosed
                        ? t("Cannot edit a closed event")
                        : (t("Only admins and creators can edit events") ?? "")
                  }
                  shouldWrapChildren
                >
                  {t("Edit event")}
                </Explanation>
              </MenuItem>
            )}
            <MenuItem
              isDisabled={!event.canEdit}
              icon={<Icon as={FaCopy} />}
              onClick={async () => {
                const res = await createEventCopy({ eventId: event.id });
                if ("data" in res) navigate(urls.events.edit.go(res.data.id).intro);
                else displayer.trigger(res.error);
              }}
            >
              <Explanation enabled={!event.canEdit} text={t("Only admins and creators can copy events") ?? ""}>
                <>
                  {t("Copy event")}
                  {isLoadingEventCopy && <Spinner />}
                </>
              </Explanation>
            </MenuItem>
            <MenuItem
              isDisabled={!event.canEdit || isAwardedBse(event) || isEventCancelled || isEventClosed}
              icon={<Icon as={FaTimes} />}
              onClick={() => setIsCancelModalOpen(true)}
            >
              <Explanation
                enabled={!event.canEdit || isAwardedBse(event) || isEventCancelled || isEventClosed}
                text={
                  isEventCancelled
                    ? t("Event is already cancelled")
                    : isEventClosed
                      ? t("Event is already closed")
                      : (t("Only admins and creators can cancel events, and awarded events cannot be cancelled") ?? "")
                }
              >
                {t("Cancel event")}
              </Explanation>
            </MenuItem>
            <MenuItem
              isDisabled={!event.canEdit || isAwardedBse(event) || isEventCancelled || isEventClosed}
              icon={<Icon as={FaCheck} />}
              onClick={() => setIsCloseModalOpen(true)}
            >
              <Explanation
                enabled={!event.canEdit || isAwardedBse(event) || isEventCancelled || isEventClosed}
                text={
                  isEventCancelled
                    ? t("Event is already cancelled")
                    : isEventClosed
                      ? t("Event is already closed")
                      : (t("Only admins and creators can close events, and awarded events cannot be closed") ?? "")
                }
              >
                {t("Mark as completed")}
              </Explanation>
            </MenuItem>
            {sub.hasNegotiationAcces && (
              <>
                <Explanation
                  enabled={
                    (!isLatestDeadlinePassed() && (isPublishedBse(event) || isCompletedBse(event))) ||
                    isEventCancelled ||
                    isEventClosed
                  }
                  text={
                    isEventCancelled
                      ? t("Cannot start negotiations on a cancelled event")
                      : isEventClosed
                        ? t("Cannot start negotiations on a closed event")
                        : (t("Cannot start a new negotiation round before the current deadline has passed") ?? "")
                  }
                >
                  <MenuItem
                    icon={<Icon as={FaRedoAlt} />}
                    isDisabled={
                      !event.canEdit ||
                      !(isPublishedBse(event) || isCompletedBse(event)) ||
                      !isLatestDeadlinePassed() ||
                      isEventCancelled ||
                      isEventClosed
                    }
                    onClick={async () => {
                      const res = await startNegotiationRound({
                        eventId: event.id,
                        createNegotiationRoundRequest: {
                          deadline: moment().endOf("day").set("ms", 0).set("s", 0).toISOString(),
                        },
                      }).unwrap();
                      navigate(urls.events.editNegotiation.go({ eventId: event.id, negotiationId: res.id }).intro);
                    }}
                  >
                    {t("New negotiation")} {isStartingNegotiationRound && <Spinner />}
                  </MenuItem>
                </Explanation>
              </>
            )}
          </MenuList>
        </Menu>
      </Flex>
      <Flex width="full" justifyContent="center" py="10">
        <Flex flexDirection="column" grow="1">
          <Flex alignItems="center" justifyContent="center" mb="2">
            <Heading as="h2" size="md" textAlign="center" mr="2">
              {event.title}
            </Heading>
            {(isEventCancelled || isEventClosed) && <EventStatusTag status={event.status} size="sm" />}
            {currentRound?.stateName === NegotiationStateName.Draft && (
              <EventStatusTag status={BseStatusDto.Draft} size="sm" />
            )}
          </Flex>
          <Flex width="full" justifyContent="center" alignItems="center" pt="2">
            <Icon
              as={isCurrentDeadlinePassed() ? FaCheckCircle : FaClock}
              color={isCurrentDeadlinePassed() ? "smSecondary" : "smPrimary"}
              w="4"
              h="4"
              mr="1.5"
            />
            <Text fontSize={"md"} color={isCurrentDeadlinePassed() ? "smSecondary" : "smPrimary"} whiteSpace="nowrap">
              {t("Bid deadline")}: {currentDeadline ? displayDateWithTime(currentDeadline, t("at")) : null}
            </Text>
          </Flex>
          <Text textAlign="center" fontSize="sm" color="smMuted" pt="1">
            {t("Created by")} <b>{`${event.createdBy.firstName} ${event.createdBy.lastName}`} </b>
            {t("on")} {displayDate(event.createdAt)}.
          </Text>
        </Flex>
      </Flex>

      {/* Cancel Event Modal */}
      <Modal isOpen={isCancelModalOpen} onClose={() => setIsCancelModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Cancel sourcing event")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb="4">
              {`${t("Are you sure you want to cancel this sourcing event")}? ${t("This action cannot be undone")}.`}
            </Text>
            <FormControl>
              <FormLabel>
                {t("Reason for cancellation")} ({t("optional")})
              </FormLabel>
              <Textarea
                placeholder={`${t("Cancelling reason")}...`}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsCancelModalOpen(false)}
              isDisabled={isCancellingSourcingEvent}
            >
              {t("Cancel")}
            </Button>
            <Button colorScheme="red" onClick={handleCancelEvent} isLoading={isCancellingSourcingEvent}>
              {t("Cancel event")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Close Event Modal */}
      <Modal isOpen={isCloseModalOpen} onClose={() => setIsCloseModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Complete without awarding")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb="4">
              {`${t("Are you sure you want to complete this sourcing event without selecting a winner")}? ${t("This action cannot be undone")}.`}
            </Text>
            <FormControl>
              <FormLabel>
                {t("Reason for completing")} ({t("optional")})
              </FormLabel>
              <Textarea
                placeholder={`${t("Completion reason")}...`}
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                rows={4}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsCloseModalOpen(false)}
              isDisabled={isClosingSourcingEvent}
            >
              {t("Cancel")}
            </Button>
            <Button colorScheme="orange" onClick={handleCloseEvent} isLoading={isClosingSourcingEvent}>
              {t("Complete event")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
